import { Location } from '@angular/common';
import { Component, NgZone, OnInit, ViewChild } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Language } from '@app/models/language.model';
import { AppInsightsService } from '@app/services/appinsights/app-insights.service';
import { ConfigurationService } from '@app/services/configuration/configuration.service';
import { LoaderService } from '@app/services/loader/loader.service';
import { UserService } from '@app/services/user/user.service';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { ConnectionStatus, Network } from '@capacitor/network';
import { filter, interval, map, Subscription, tap } from 'rxjs';
import { AppNavigationModel } from 'src/app/models/appnavigation.model';
import { User } from 'src/app/models/user.model';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ClosedownService } from 'src/app/services/closedown/closedown.service';
import { NavigationService } from 'src/app/services/navigation/navigation.service';
import { UtilsService } from 'src/app/services/util/util-service';
import { UserType } from 'src/app/shared/enums';
import { environment } from 'src/environments/environment';
import { LocalizationService } from '../internationalization/localization.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
  matSizeNavHeightClass = "mat-sidenav-web-with-mat-toolbar-height";
  currentUser: User | undefined;
  languages: Language[] = [];
  mainClass = "main-content-status-bar";
  deviceInfo: any = null;
  appNavigationModel: AppNavigationModel | undefined;
  appEnvironment = environment.appEnvironment.toLocaleLowerCase();
  workBench: string = 'WORKBENCH';
  isResourceAdmin: boolean = false;
  isMobileAndTablet: boolean = false;
  termsCondition = ``;
  privacyPolicy = ``;
  isNetworkAvailabile = true;
  version = environment.appVersion;
  isSearchIndexingBlocked = environment.blockSearchIndexing;
  isCityAdmin: boolean = false;
  @ViewChild('sidenav') matSidenav: MatSidenav | undefined;

  private userIdleSub?: Subscription;

  constructor(private _utilService: UtilsService,
    private _localizationService: LocalizationService,
    private _zone: NgZone,
    private _router: Router,
    private _navigationService: NavigationService,
    private _location: Location,
    private _authServices: AuthService,
    private _closedownService: ClosedownService,
    private _loader: LoaderService,
    private _swUpdate: SwUpdate,
    private _meta: Meta,
    private _appInsightsService: AppInsightsService,
    private titleService: Title,
    private _configurationService: ConfigurationService,
    private _userService: UserService) {

    this._configurationService.getTermsConditionLink().then(t => {
      this.termsCondition = t;
    });

    this._configurationService.getPrivacyPolicyLink().then(t => {
      this.privacyPolicy = t;
    });


    this._router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => {
        let route: ActivatedRoute = this._router.routerState.root;
        let routeTitle = '';
        while (route!.firstChild) {
          route = route.firstChild;
        }
        if (route.snapshot.data['title']) {
          routeTitle = route!.snapshot.data['title'];
        }
        return routeTitle;
      })
    ).subscribe((data: any) => {
      if (data) {
        this.titleService.setTitle('City Onsite - ' + data);
      }
    });

    this.onLangSelect();
    this.updateClient();
    this.initConnection();
    this.initializeApp();
    this.isMobileAndTablet = this._utilService.isMobileDevice();
  }

  initializeApp() {
    this._authServices.isAuthenticated().then(auth => {
      if (auth) {
        this._authServices.getUser()
          .then(user => {
            this._appInsightsService.setUserId(user.emailAddress);
            this.currentUser = user;
          });
      } else {
        this._appInsightsService.clearUserId();
      }
    });
    
    this._navigationService.messageSubject.subscribe({
      next: model => {
        this.appNavigationModel = model;
        if (!this.isMobileAndTablet) {
          if (!this.appNavigationModel.enableMenu && !this.appNavigationModel.enableBackButton) {
            if (this.appNavigationModel.remainHeaderPadding) {
              this.mainClass = "main-content-status-bar-web-with-mat-toolbar";
              this.matSizeNavHeightClass = "mat-sidenav-web-with-mat-toolbar-height";
            } else {
              this.mainClass = "main-content-status-bar-web-no-mat-toolbar";
              this.matSizeNavHeightClass = "mat-sidenav-web-no-mat-toolbar-height";
            }
          } else {
            this.mainClass = "main-content-status-bar-web";
            this.matSizeNavHeightClass = "mat-sidenav-web-with-mat-toolbar-height";
          }
        } else {
          this.mainClass = "main-content-status-bar";
          this.matSizeNavHeightClass = "";
        }
      }
    });
    this.initAppListerner();
  }

  initAppListerner() {
    Network.addListener('networkStatusChange', (status) => {
      this._zone.run(() => {
        this.setNetworkIfAvailable(status);
      });
    });

    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this._zone.run(() => {
        const domain = environment.appDomain;
        if (event.url.indexOf(`${environment.appName}://auth`) > -1) {
          if (this._loader.isLoading) {
            this._loader.hide();
          }
          this._router.navigate(['login']);
        } else if (event.url.indexOf(`${environment.appName}://userprofile`) > -1) {
          this._router.navigate(['userprofile']);
        } else {
          const pathArray = event.url.split(domain);
          const appPath = pathArray.pop();
          if (appPath) {
            this._router.navigateByUrl(appPath);
          }
        }
      });
    });

    App.addListener('backButton', ({ canGoBack }) => {
      if (confirm(this._localizationService.translate('appcomponent_dialog_exit'))) {
        if (this.isMobileAndTablet) {
          App.exitApp();
        } else {
          this._authServices.logout().then(r => {
            this._router.navigate(['login']);
          });
        }
      }
    });

  }

  ngOnInit(): void {

    if (!this._utilService.isMobileAndTablet()) {
      this._authServices.handleMsalCallBack();
    }

    this.initIdle();
    if (this.isSearchIndexingBlocked) {
      this._meta.addTag(
        { name: 'robots', content: 'noindex' }
      );
    }
    this.getResourceAdminState();
    this.getCityAdminState();
  }

  async initIdle(){

    var isEnable = await this._configurationService.getIsIdleCheckingEnable();
    if(isEnable)
    {
      var timer = await this._configurationService.getIdleCheckingFrequencySeconds();
        this.userIdleSub = interval(timer).subscribe(() => {
          this._authServices.getUser().then(u => {
            if(u){
              this._userService.getCurrentUserSession(true).subscribe(async (isValidSession) => {
                if (!isValidSession) {
                  this._authServices.logout().then(() => {
                    alert(this._localizationService.translate('loginuser_signin_anotherdevice'));
                    this._router.navigate(['login']);
                  });
                }
              });
            }
          });
        });
    }


  }

  getResourceAdminState() {
    this._authServices.getResourceAdminState().subscribe(isAdmin => {
      this.isResourceAdmin = isAdmin;
    });
  }

  async getCityAdminState() {
    this._authServices.isAuthenticated().then(async auth => {
      if (auth) {
        const roles = await this._authServices.getUserRoles();
        if (roles && roles.length > 0 && roles.includes(UserType.CITY_ADMIN)) {
          this.isCityAdmin = true;
        }
      }
    });

  }

  onLangSelect(): void {
    this._localizationService.get().then((l) => {
      if(l){
        this._localizationService.useLanguage(l);
      }else{
        this._localizationService.set(environment.defaultLang).then(() => {
          this._localizationService.useLanguage(environment.defaultLang);
        });
        this._localizationService.setAzureLocale(environment.defaultAzureLocale);
      }
    });
   
  }

  logOutOnClick() {
    if (confirm(this._localizationService.translate("appcomponent_dialog_logout"))) {
      this.appNavigationModel = undefined;
      this._router.navigate(['logout']);
    }
  }

  userProfileOnClick() {
    this._router.navigate(['userprofile']);
    this.matSidenav?.close();
  }

  userDocumentsOnClick() {
    this._router.navigate(['userdocuments']);
    this.matSidenav?.close();
  }

  releaseNoteCityOnsiteOnClick() {
    this._router.navigate(['releasenote', 'city onsite']);
    this.matSidenav?.close();
  }

   releaseNoteWorkbenchOnClick() {
    this._router.navigate(['releasenote', 'workbench']);
    this.matSidenav?.close();
  }

  termsConditionOnClick() {
    window.open(this.termsCondition, '_blank');
    this.matSidenav?.close();
  }

  privacyPolicyOnClick() {
    window.open(this.privacyPolicy, '_blank');
    this.matSidenav?.close();
  }

  needHelpOnClick() {
    this._configurationService.getNeedHelpDocUrl().then(t => {
      window.open(t, '_blank');
      this.matSidenav?.close();
    });
  }

  workBenchOnClick() {
    if (this.currentUser && this.isResourceAdmin) {
      if (environment.isNewFlow) {
        this._router.navigate(['dashboard']);
      } else {
        this._closedownService.directToClosedown(false);
      }
      this.matSidenav?.close();
    }
  }

  userManagementOnClick() {
    this._router.navigate(['usermanagementlist']);
    this.matSidenav?.close();
  }

  siteManagementOnClick(){
    this._router.navigate(['sitemanagementlist']);
    this.matSidenav?.close();
  }

  back() {
    this._location.back();
  }

  async initConnection(): Promise<void> {
    const status = await Network.getStatus();
    this.setNetworkIfAvailable(status);
  }

  setNetworkIfAvailable(status: ConnectionStatus) {
    if (status.connectionType === 'none')
      this.isNetworkAvailabile = false;
    else
      this.isNetworkAvailabile = true;
  }

  updateClient() {
    if (this._swUpdate.isEnabled) {
      this._swUpdate.versionUpdates
        .pipe(
          filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
          tap(() => {
            this._swUpdate.activateUpdate().then(() => window.location.reload());
          })
        )
        .subscribe();
    }
  }

  adminManagementOnClick() {
    this._router.navigate(['adminmanagementlist']);
    this.matSidenav?.close();
  }

  visitorTypeManagementOnClick() {
    this._router.navigate(['visitortypemanagement']);
    this.matSidenav?.close();
  }

  ngOnDestroy() {
    if (this.userIdleSub) {
      this.userIdleSub.unsubscribe();
    }
  }
}